import { LRUMap } from 'lru_map';
import Deferred from 'utils/deferred';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { performance } from 'utils/performance';
import { LRUSWCache } from 'modules/LRUSWCache';

export const NRRD_MASK_CACHE_KEY = 'nrrd-mask-downloader-v1';

// Masks are per-case, AI-segmentation outputs that would otherwise accumulate unbounded across a
// session (see VX-4241). Cap the store and evict least-recently-used masks first, mirroring the
// apollo-pre-cache bound in modules/Apollo/links/precacheLink.ts.
const maskCache = new LRUSWCache({
  key: NRRD_MASK_CACHE_KEY,
  version: '1',
  maxSize: 500 * 1024 * 1024, // 500 MB
  evictionThreshold: 0.1,
});

/**
 * Similar to Cache#open but supports the `credentials: include` fetch option.
 */
const open = async (
  cache: LRUSWCache,
  url: string
): Promise<{
  buffer: ArrayBuffer;
  fromCache: boolean;
}> => {
  const startTimestamp = performance.now();

  // If it's supported, we retrieve the cache and check if the URL we are looking for
  // has been cached already, in case, we load it from cache. Otherwise we
  // fetch the resource from the network and put it in the cache for the next time.
  const cacheResponse = await cache.match(url);

  if (cacheResponse != null) {
    analytics.track(viewer.sys.nrrdMaskCacheHit, {
      duration_ms: Math.round(performance.now() - startTimestamp),
      url,
    });

    return { buffer: await cacheResponse.arrayBuffer(), fromCache: true };
  }

  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new TypeError('bad response status');
  }

  analytics.track(viewer.sys.nrrdMaskDownload, {
    duration_ms: Math.round(performance.now() - startTimestamp),
    url,
  });

  const forCache = response.clone();
  const buffer = await response.arrayBuffer();
  // No need to await for the cache.put since we are not going to read it right afterwards, other
  // calls will just wait for it. NRRD responses may omit content-length, so pass the measured byte
  // length explicitly rather than relying on LRUSWCache's header read.
  cache.put(url, forCache, buffer.byteLength);

  return { buffer, fromCache: false };
};

// This jobs map allows us to reuse existing inflight requests
// rather than attempting to download or untar data twice.
type JobPayload = {
  buffer: ArrayBuffer;
  fromCache: boolean;
  length: number;
};
const jobs = new LRUMap<string, Promise<JobPayload>>(20);

// Clears both the LRUSWCache registry (localStorage) and the underlying CacheStorage entries,
// mirroring purgePrecache in modules/Apollo/links/precacheLink.ts. On logout, the generic
// caches.keys() wipe in useClearStoredDataOnClose clears the CacheStorage cache but not this
// registry -- without this, the registry would keep reporting stale entries/sizes until the
// next mask download self-heals it via LRUSWCache's orphan-recovery path.
export async function purgeNrrdMaskCache(): Promise<void> {
  return maskCache.purge();
}

/**
 * Reads the cache response if it exists and returns a clone of the original array buffer
 * to allow users of the buffer to modify in place if needed without impacting the cache.
 */
export const downloadNrrdMask = async (nrrdUrl: string): Promise<JobPayload> => {
  if (jobs.has(nrrdUrl)) {
    const job = await jobs.get(nrrdUrl);

    if (job?.buffer == null || job?.buffer.byteLength !== job.length) {
      throw new Error('An unexpected error occurred while retrieving the NRRD mask');
    }
    return {
      buffer: job.buffer.slice(0),
      fromCache: job.fromCache,
      length: job.buffer.byteLength,
    };
  }
  const deferred = new Deferred<JobPayload>();
  jobs.set(nrrdUrl, deferred.promise);

  /**
   * Given a series SMID, returns an archive of DICOMs that compose it
   */
  const cache = await maskCache.open();
  const { buffer, fromCache } = await open(cache, nrrdUrl);

  deferred.resolve({ buffer: buffer.slice(0), fromCache, length: buffer.byteLength });

  return { buffer: buffer.slice(0), fromCache, length: buffer.byteLength };
};
